// Order a Test — the clinician-facing ordering flow. 5-step stepper
// (confirmed design: a single long scroll is exactly what gets abandoned
// or fat-fingered on a form this dense). Ties together: the independent
// test catalog (/clinician/orderable-tests), clinician_patient_records
// (/clinician/patients), the dynamic per-test requisition schema, and the
// clinician's stored signature (consented at registration).
//
// 2026-10-09 security fix — the requisition schema used to be fetched
// with a direct, unauthenticated browser call to labariumaireport's
// public GET /api/order-requisition/schema/{schema_key}. That's now
// routed through this app's own GET /clinician/order-requisition-schema/
// {schema_key} proxy instead (clinician-authenticated, same as every
// other /clinician/* call below) — labariumaireport's endpoint is no
// longer reachable unauthenticated at all.
//
// 2026-10-08 — in-office patient signing removed (founder decision):
// EVERY order now goes through the async, no-login patient-consent link
// (PatientConsent.js) as the only place a patient ever signs or gives
// their own consent. The clinician never captures a live patient
// signature here anymore, and Step 4 no longer collects patient_consent/
// biobank_consent — those moved to the patient's own page.
//
// Order a Test, Part B — multi-select, multi-family. A clinician can now
// select any combination of tests across any number of families in one
// order; Step 3 renders one requisition section PER DISTINCT FAMILY
// actually selected (deduped by schema_key, since e.g. 2 Proove sub-
// tests share one schema/form), plus the shared patient/payment sections
// exactly once. Submission fires one render call per distinct schema_key.
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Stepper, Step, StepLabel, Button, Autocomplete,
  TextField, Card, CircularProgress, Alert, Checkbox, FormControlLabel,
  Chip, Accordion, AccordionSummary, AccordionDetails, createFilterOptions,
  Select, MenuItem, IconButton,
} from "@mui/material";
import { ExpandMore, Science, CheckCircle, Search, Medication, Coronavirus, Close, Receipt, Visibility } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import UserContext from "./UserContext";
import { AddressFields, useAddressConfirm } from "./addressShared";
import DynamicRequisitionForm, { validateSchemaRequired } from "./DynamicRequisitionForm";
import { extractErrorDetail } from "./validators";
import ClinicianPageHeader from "./ClinicianPageHeader";
import {
  PROOVE_PGX_SCHEMA_KEY, TEST_ID_TO_PROFILE_PREFIX, distinctFamilySchemas,
  effectiveSelectedTests, proovePresentWithNoProfileChecked, PROOVE_NO_PROFILE_CHECKED_MESSAGE,
  toxicologyPresentWithNoOptionSelected, TOXICOLOGY_NO_OPTION_SELECTED_MESSAGE,
  infectiousDiseasePresentWithNoOptionSelected, INFECTIOUS_DISEASE_NO_OPTION_SELECTED_MESSAGE,
  familyPresentWithNothingRequiredSelected, genericFamilyPreCheckUpdates,
  mergeLastOrderPrefill, stripAppliedPrefill, patientRecordAutofillSource,
  PATIENT_RECORD_CLINICAL_FIELD_IDS,
} from "./orderTestDerivation";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#00357a", navyDark: "#001f4d", navyMid: "#004299", gold: "#c9a96e", goldLight: "#dfc08a" };

// Converted to a function (2026-10-13, dark-mode fix) so each call site
// can pass the real isDark value — this used to be a flat module-level
// constant with light-only colors baked in, which is part of why this
// page's dark mode never worked despite rendering a working ThemeToggle.
const accordionSx = (isDark) => ({
  mb: 1.5, "&:before": { display: "none" },
  border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,53,122,0.1)",
  borderRadius: "10px !important", overflow: "hidden", bgcolor: isDark ? "#111827" : "#fff",
});

const STEPS = ["Select Patient", "Select Tests", "Patient & Billing", "Clinical Details", "Review & Sign"];

// Supporting Documents — optional, order-level uploads (patient ID,
// insurance card, driver's license, ABN, prior auth, or anything else),
// never required to place an order. Mirrors the backend's own
// SUPPORTING_DOC_TYPES allowlist in labariumaiportal/backend/main.py.
const SUPPORTING_DOC_TYPE_OPTIONS = [
  { value: "patient_id", label: "Patient ID" },
  { value: "insurance_card", label: "Insurance Card" },
  { value: "drivers_license", label: "Driver's License" },
  { value: "abn", label: "ABN" },
  { value: "pa", label: "PA — Prior Auth" },
  { value: "other", label: "Other" },
];
const emptySupportingDocRow = () => ({ _key: Math.random().toString(36).slice(2), docType: "", otherLabel: "", file: null });

// Human-readable group/section labels for the 3 requisition families
// (Order a Test, Part A). Raw keys are what `family`/the schema_key
// prefix carry on the wire — these are presentation-only.
const FAMILY_LABELS = {
  proove: "Proove",
  toxicology: "Toxicology",
  infectious_disease_molecular: "Infectious Disease / Molecular PCR",
};
const FAMILY_FILTER_ORDER = ["all", "proove", "toxicology", "infectious_disease_molecular"];

// Visual pass (2026-10-08) — one small, tasteful accent color + icon per
// requisition family, reused everywhere a clinician needs to tell
// families apart at a glance in the multi-select/multi-family flow
// (Step 1's grouped dropdown, Step 3's per-family sections, Step 4's
// review list). No family-badge colors existed anywhere else in the app
// to reuse, so these were established now — kept inside the existing
// navy/gold brand weight rather than introducing a loud new palette.
// `MyPatients.js` mirrors this exact map (can't share a module without
// creating a new file, which is out of scope for a pure styling pass).
// 2026-10-13 fix — real regression caught by review: `accent` is used
// both as a `bgcolor` (a filled tile with a white glyph on top — fine
// against either theme) AND directly as a text/icon `color` in several
// places (the family card title, the Review family label, the
// Autocomplete group header, the selected-chip icon/border). Proove's
// accent is BRAND.navy itself — unreadable as TEXT once the surrounding
// card actually goes dark (the exact "navy-on-navy" problem). `textDark`
// is the color to use instead whenever accent is being used as text/icon
// color in dark mode (see familyMeta below) — a distinct, readable
// variant per family, not one shared color that would make all 3 look
// the same.
const FAMILY_META = {
  proove: { accent: BRAND.navy, textDark: "#8ab4f8", icon: Science },
  toxicology: { accent: "#8a6d1f", textDark: BRAND.goldLight, icon: Medication },
  infectious_disease_molecular: { accent: "#0f766e", textDark: "#5eead4", icon: Coronavirus },
};
const FAMILY_META_DEFAULT = { accent: "#64748b", textDark: "#94a3b8", icon: Science };
// Returns `{ accent, textAccent, icon }` — `accent` for a filled/bgcolor
// use (unchanged by theme), `textAccent` for anywhere accent is used as
// TEXT or icon color (swaps to `textDark` in dark mode).
const familyMeta = (family, isDark) => {
  const meta = FAMILY_META[family] || FAMILY_META_DEFAULT;
  return { ...meta, textAccent: isDark ? meta.textDark : meta.accent };
};
// Reverse lookup from the Autocomplete's rendered group LABEL (what
// MUI's renderGroup actually hands back, not the raw family key) to the
// family key, so the custom group header can still look up its accent.
const FAMILY_LABEL_TO_KEY = Object.fromEntries(Object.entries(FAMILY_LABELS).map(([k, v]) => [v, k]));

// Shared section ids — byte-identical content across all 3 schemas (Part
// A's SHARED_SECTIONS). Rendered exactly ONCE regardless of how many
// families are in this order, from whichever family's schema happened to
// be fetched first — never re-rendered per family.
const SHARED_SECTION_IDS = ["ordering_provider", "patient_info", "payment_info"];

// PROOVE_PGX_SCHEMA_KEY and TEST_ID_TO_PROFILE_PREFIX now live in
// orderTestDerivation.js (imported above) alongside distinctFamilySchemas
// and the new effectiveSelectedTests derivation — see that module's
// header comment for why.

// MUI's default Autocomplete filter, wrapped so the family chip buttons
// above the search box narrow visible options WITHOUT ever removing an
// already-selected test from `options` itself — swapping `options`
// directly would make MUI warn about (and potentially drop) a selected
// value that briefly isn't in the visible list when the filter changes.
const baseAutocompleteFilter = createFilterOptions();

function patientToFormState(p) {
  return {
    first_name: p.first_name || "", last_name: p.last_name || "", dob: p.dob || "",
    address: { line1: p.address_line1 || "", unit: p.address_unit || "", city: p.address_city || "", state: p.address_state || "", zip: p.address_zip || "" },
    email: p.email || "", phone: p.phone || "",
  };
}

// Idempotency-key protection (2026-10-08) — a fresh key per genuinely new
// order attempt, generated once when the clinician reaches Review & Sign
// (never regenerated on re-render or on navigating back/forward within
// the SAME attempt) and sent with the submission so the backend can
// treat a double-click, a dropped-network retry, or a post-timeout
// resubmit as a safe no-op replay instead of a second order.
// crypto.randomUUID only exists in secure contexts (https, or localhost);
// the getRandomValues fallback covers everything else without ever
// blocking order submission over it.
export function generateIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0"));
    return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10, 16).join("")}`;
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// Review & Sign PDF preview (2026-10-13) — the real gap this closes: this
// step used to show only the HTML summary box + per-family test-name list
// above, with NO way to see the actual rendered requisition a clinician is
// about to legally sign. Ports labariumaireport/frontend's LabOrdersView.js
// InlinePreviewFrame pattern (signed URL fetched on demand -> rendered in
// an iframe, fade-in on load, 4s fallback for browsers that don't fire
// onLoad reliably inside an iframe) — same frame styling/behavior, just
// fed a same-origin blob: URL instead of a remote signed GCS URL, since
// there is no persisted GCS blob to sign a URL for before this order has
// even been submitted (see POST /clinician/orders/preview-requisition-pdf,
// which renders on the fly from the in-progress form data and never
// writes to GCS/Mongo).
function InlinePreviewFrame({ url, onError }) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
    const t = setTimeout(() => setLoaded(true), 4000);
    return () => clearTimeout(t);
  }, [url]);

  const frameSx = {
    mt: 1, borderRadius: "12px", overflow: "hidden",
    height: "min(600px, 60vh)",
    boxShadow: "0 8px 40px rgba(0,0,0,0.12)", border: "1px solid rgba(0,53,122,0.08)",
    bgcolor: "#f4f7ff",
  };
  const fadeStyle = { opacity: loaded ? 1 : 0, transition: "opacity 0.25s ease" };

  return (
    <Box sx={{ ...frameSx, display: "flex" }}>
      <iframe
        key={url} src={url} title="Requisition Preview"
        style={{ width: "100%", height: "100%", border: "none", ...fadeStyle }}
        onLoad={() => setLoaded(true)} onError={onError}
      />
    </Box>
  );
}

// Unlike LabOrdersView's GET-signed-URL version (a 30-minute server-side
// expiry it has to guard against), this one POSTs the clinician's
// CURRENT, in-progress form data on every (re)fetch and turns the
// response into a local blob: URL — there's no server-side expiry to
// guard against, but there IS a "went stale because the clinician edited
// the form" risk instead, which a time-based guard can't catch at all.
// `depsSignature` (a JSON string of everything that actually feeds the
// rendered PDF for this family) is compared against the last fetch's
// signature on every toggle-open, so stepping back to Step 4 after
// editing Step 3/4's clinical details always re-renders instead of
// silently replaying a stale preview from before the edit.
function RequisitionPreviewToggle({ title, schemaKey, previewParams, depsSignature, authHeaders }) {
  // 2026-10-13 fix — real regression caught by review: this is a module-
  // level component, so it never received the parent's `isDark` and its
  // "Preview {title}" button stayed hardcoded BRAND.navy — unreadable
  // once Step 4's surrounding card actually went dark. useTheme() directly
  // here (rather than threading an isDark prop down through every call
  // site) since this component is otherwise self-contained.
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [open, setOpen] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const lastFetchedSignatureRef = useRef(null);
  const blobUrlRef = useRef(null);

  // Revoke the object URL on unmount (and whenever a new one replaces it,
  // inside fetchPreview below) so an abandoned preview doesn't leak
  // memory for the rest of the session.
  useEffect(() => () => { if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current); }, []);

  const fetchPreview = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(
        `${API_BASE}/clinician/orders/preview-requisition-pdf`,
        previewParams,
        { headers: authHeaders, responseType: "blob" },
      );
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      const url = URL.createObjectURL(res.data);
      blobUrlRef.current = url;
      setBlobUrl(url);
      lastFetchedSignatureRef.current = depsSignature;
    } catch (err) {
      // axios with responseType "blob" hands back the error body as a
      // Blob too, not parsed JSON — has to be read back out explicitly to
      // surface the backend's real detail string instead of a generic
      // failure message.
      let detail = "Could not load preview right now.";
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          if (typeof parsed.detail === "string") detail = parsed.detail;
        } catch {
          // leave the generic message — body wasn't JSON (e.g. a gateway error page)
        }
      } else if (typeof data?.detail === "string") {
        detail = data.detail;
      }
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    const stale = !blobUrl || lastFetchedSignatureRef.current !== depsSignature;
    if (stale && !loading) await fetchPreview();
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Button size="small" startIcon={<Visibility sx={{ fontSize: 15 }} />}
        onClick={toggle} sx={{ textTransform: "none", fontSize: 12.5, fontWeight: 700, color: isDark ? BRAND.gold : BRAND.navy }}>
        {open ? "Hide Preview" : `Preview ${title}`}
      </Button>
      {open && (
        loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={24} sx={{ color: isDark ? BRAND.gold : BRAND.navy }} /></Box>
        ) : error ? (
          <Alert severity="warning" sx={{ mt: 1 }}
            action={<Button size="small" onClick={fetchPreview} sx={{ textTransform: "none" }}>Retry</Button>}>
            {error}
          </Alert>
        ) : blobUrl ? (
          <InlinePreviewFrame url={blobUrl} onError={() => setError("Preview could not be displayed — try Retry.")} />
        ) : null
      )}
    </Box>
  );
}

export default function OrderTest() {
  // 2026-10-13 fix — this page hardcoded light-mode colors throughout
  // despite rendering a working ThemeToggle (via ClinicianPageHeader) —
  // useTheme()/isDark were entirely absent here, same gap as Settings.js/
  // MySummaries.js/MyPatients.js. Same useTheme() pattern Login.js/
  // Register.js already use correctly.
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const token = user?.token;
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [orderResult, setOrderResult] = useState(null);
  // Supporting Documents — optional, order-level uploads collected on the
  // Review & Sign step. Never blocks "Place Order"; files are uploaded
  // AFTER the order is placed, once order_id is known (see
  // handleSubmitOrder's upload pass below).
  const [supportingDocRows, setSupportingDocRows] = useState([emptySupportingDocRow()]);
  const [supportingDocUploadErrors, setSupportingDocUploadErrors] = useState([]);
  const [uploadingSupportingDocs, setUploadingSupportingDocs] = useState(false);
  // Idempotency-key protection — generated once per order attempt (see
  // generateIdempotencyKey above), set the first time Review & Sign is
  // reached, never regenerated by re-renders or by stepping back/forward
  // within the same attempt. Cleared in resetForNextOrder so a genuinely
  // NEW order (after a successful submission, "Order Another Test") gets
  // its own fresh key.
  const [idempotencyKey, setIdempotencyKey] = useState(null);
  // Labarium identity plan — second, independent address-confirmation
  // safety net (see addressShared.js's useAddressConfirm docstring).
  const { confirmAddress, dialog: addressConfirmDialog } = useAddressConfirm(API_BASE);

  // Step 2 — tests (plural now). `familyFilter` drives the chip row above
  // the search box ("All" / one per family actually present in the
  // catalog) — purely a client-side narrowing of visible options, no
  // extra API call.
  const [tests, setTests] = useState([]);
  const [selectedTests, setSelectedTests] = useState([]);
  const [familyFilter, setFamilyFilter] = useState("all");
  const [recommendedTestIds, setRecommendedTestIds] = useState([]);

  // Step 1 — patient
  const [patients, setPatients] = useState([]);
  const [patientsTruncated, setPatientsTruncated] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isNewPatient, setIsNewPatient] = useState(false);
  const [autofillEnabled, setAutofillEnabled] = useState(true);
  // Billing/demographic autofill (separate from, and additive to, the
  // identity-field autofill above via patientToFormState/autofillEnabled —
  // Home.js's "Returning patients autofill" card text refers to that
  // existing, identity-only feature; this extends autofill to bill_to/
  // policy/insurance/sex/height/race/etc., which that feature never
  // covered). 2026-10-10 — now reads PRIMARILY off the patient record's
  // own fields (patientRecordAutofillSource in orderTestDerivation.js),
  // falling back to this patient's MOST RECENT previous order (GET
  // /clinician/patients/{id}/last-order-data) only for a patient whose
  // record predates that change. `lastOrderPrefillNotice` drives Step 3/4's
  // "pre-filled, please review" banner near the shared section — cleared
  // whenever a brand-new fetch starts or a new/different patient is
  // picked, so it never shows stale context for the wrong patient. Kept
  // its original name even though it now also fires for the record-source
  // path, to avoid touching every reference below for a rename alone.
  const [lastOrderPrefillNotice, setLastOrderPrefillNotice] = useState(false);
  const lastOrderFetchSeq = useRef(0);
  // Which sharedClinicalData fields (and the exact value) THIS feature
  // itself wrote in, for the CURRENTLY selected patient — so that if the
  // clinician switches to a different patient (or to "+ New Patient")
  // before submitting, whatever this feature injected for the OLD patient
  // gets stripped back out (via stripAppliedPrefill) rather than riding
  // along into the new patient's order. Anything the clinician edited
  // after the prefill landed is left alone — stripAppliedPrefill only
  // removes a field whose value is still EXACTLY what was injected.
  const appliedPrefillFieldsRef = useRef({});

  useEffect(() => {
    axios.get(`${API_BASE}/clinician/settings`, { headers: authHeaders })
      .then((res) => setAutofillEnabled(res.data.order_form_autofill_enabled !== false))
      .catch(() => {});
  }, [authHeaders]);

  // Step 3 — patient demographics (payment_info is collected as part of
  // sharedClinicalData in Step 4's accordion, not separate state here).
  const [patientForm, setPatientForm] = useState(patientToFormState({}));

  // Step 4 — clinical. Two buckets, deliberately kept separate:
  //   sharedClinicalData — the ONE copy of the shared patient_info-extras/
  //     payment_info fields (sex, bill_to, etc.), filled once regardless
  //     of family count, and merged into EVERY family's submitted_data
  //     server-side (several of these fields are `required: True` in all
  //     3 schemas, e.g. sex/bill_to — this is genuinely shared content,
  //     not "pick one family's copy").
  //   clinicalDataByFamily — keyed by schema_key, since Toxicology and
  //     Infectious Disease both define a `specimen_collection` section
  //     with colliding field ids (specimen_type/collector_name/
  //     collection_date/collection_time); flattening these into one
  //     object would cross-contaminate two families' answers if both
  //     were ever selected in the same order.
  const [schemasByKey, setSchemasByKey] = useState({});
  const [sharedClinicalData, setSharedClinicalData] = useState({});
  const [clinicalDataByFamily, setClinicalDataByFamily] = useState({});

  // Step 5 — sign. No provider SignaturePad here — the ordering provider's
  // signature comes from their stored signature on file (captured at
  // registration / managed in Settings), stamped via the consent checkbox
  // below, not re-drawn per order (confirmed design — re-signing every
  // order isn't required or meaningful).
  //
  // 2026-10-08 — no patient SignaturePad here either, anymore: in-office
  // patient signing was removed. There used to be a patientSigRef,
  // patientConsent, and biobankConsent state trio here, all three now
  // living instead in PatientConsent.js (the patient's own async signing
  // page), moved verbatim along with the legal text they're attached to
  // — not duplicated in both places.
  const [providerConsent, setProviderConsent] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/clinician/orderable-tests`, { headers: authHeaders })
      .then((res) => setTests(res.data.tests || []))
      .catch(() => setError("Could not load the test catalog."));
  }, [authHeaders]);

  // 2026-10-15 fix (client-facing audit-log completeness sweep finding) —
  // a genuine fetch failure here used to be silently swallowed, leaving
  // `patients` empty with no indication why — indistinguishable from this
  // clinician genuinely having zero patients on file yet, in the one
  // picker that's the entry point for ordering a test at all.
  const [patientsLoadError, setPatientsLoadError] = useState(false);
  const loadPatients = useCallback(() => {
    setPatientsLoadError(false);
    axios.get(`${API_BASE}/clinician/patients`, { headers: authHeaders })
      .then((res) => {
        setPatients(res.data.patients || []);
        // 2026-10-14 fix — same gap as MyPatients.js had: the backend caps
        // this unfiltered list at 500 and sets `truncated: true` precisely
        // so the UI can say so (GET /clinician/patients, backend/main.py
        // ~5711-5754) — this picker was silently ignoring it, so a patient
        // past the first 500 (alphabetically/insertion order) simply never
        // appeared in this dropdown with no indication why.
        setPatientsTruncated(!!res.data.truncated);
      })
      .catch(() => setPatientsLoadError(true));
  }, [authHeaders]);
  useEffect(() => { loadPatients(); }, [loadPatients]);

  // Tier 1 recommendation (Part 11) — fully hardcoded, no AI: once a
  // patient is selected, show whichever active/orderable tests they
  // haven't already been ordered, as "recommended". Confirmed with the
  // user: "if a doctor has ordered one Proove test... show the others as
  // recommended" — simple completion logic, nothing speculative.
  useEffect(() => {
    if (!selectedPatient || !tests.length) { setRecommendedTestIds([]); return; }
    axios.get(`${API_BASE}/clinician/orders`, { headers: authHeaders, params: { clinician_patient_id: selectedPatient.id } })
      .then((res) => {
        const ordered = new Set((res.data.orders || []).flatMap((o) => o.test_ids || (o.test_id ? [o.test_id] : [])));
        setRecommendedTestIds(tests.map((t) => t.test_id).filter((id) => !ordered.has(id)));
      })
      .catch(() => setRecommendedTestIds([]));
  }, [selectedPatient, tests, authHeaders]);

  // Distinct families actually in play, in first-selected order — Step
  // 3/4's rendering and the schema-fetch effect below both key off this.
  // Deliberately still based on Step 1's RAW selectedTests, not the
  // effective/reconciled set below: which schemas to fetch/render as
  // FORM SECTIONS never changes just because a clinician checks/unchecks
  // a profile inside a form that's already on screen.
  const familySchemasUsed = useMemo(() => distinctFamilySchemas(selectedTests), [selectedTests]);

  // Bug fix, 2026-10-09 — the EFFECTIVE Proove selection, reconciled
  // against whichever profile_selection checkboxes are actually checked
  // in Step 3/4's form (see orderTestDerivation.js's own comment for the
  // full story). This is what the Review step must display and what
  // handleSubmitOrder must send as `test_ids` — `selectedTests`/
  // `familySchemasUsed` above stay Step-1-only on purpose, since they
  // also drive which schemas get fetched/rendered as form sections.
  const { tests: effectiveTests, missingTestIds: effectiveMissingTestIds } = useMemo(
    () => effectiveSelectedTests(selectedTests, clinicalDataByFamily, tests, schemasByKey),
    [selectedTests, clinicalDataByFamily, tests, schemasByKey]
  );
  const effectiveFamilySchemasUsed = useMemo(() => distinctFamilySchemas(effectiveTests), [effectiveTests]);
  const effectiveTestNamesSummary = effectiveTests.map((t) => t.display_name).join(", ");
  // 2026-10-09, part 2 — generalized from proovePresentNoneChecked: fires
  // for Proove exactly as before, PLUS any other family whose own
  // schema-driven selection field is `required` and ends up with nothing
  // resolved.
  // 2026-10-14 fix — Toxicology's and Infectious Disease's REAL one-of
  // groups (the backend's own _REQUIRE_ONE_OF_BY_SCHEMA — different
  // fields than the still-not-required test_id-mapped single-select
  // fields the paragraph above refers to) now ALSO block here, with their
  // own specific messages (byte-identical to the backend's), checked
  // before falling through to the generic one below.
  const blockedSelectionMessage = useMemo(() => {
    if (proovePresentWithNoProfileChecked(selectedTests, clinicalDataByFamily)) return PROOVE_NO_PROFILE_CHECKED_MESSAGE;
    if (toxicologyPresentWithNoOptionSelected(selectedTests, clinicalDataByFamily)) return TOXICOLOGY_NO_OPTION_SELECTED_MESSAGE;
    if (infectiousDiseasePresentWithNoOptionSelected(selectedTests, clinicalDataByFamily)) return INFECTIOUS_DISEASE_NO_OPTION_SELECTED_MESSAGE;
    if (familyPresentWithNothingRequiredSelected(selectedTests, clinicalDataByFamily, schemasByKey)) {
      return "At least one selection is required for one of the selected tests, but none is currently selected in its clinical details form.";
    }
    return null;
  }, [selectedTests, clinicalDataByFamily, schemasByKey]);

  useEffect(() => {
    familySchemasUsed.forEach(({ schemaKey }) => {
      if (schemasByKey[schemaKey]) return; // already fetched/cached — a 2nd Proove sub-test never re-fetches proove_pgx_v1.
      axios.get(`${API_BASE}/clinician/order-requisition-schema/${schemaKey}`, { headers: authHeaders })
        .then((res) => setSchemasByKey((prev) => ({ ...prev, [schemaKey]: res.data.schema })))
        .catch(() => setError("Could not load the requisition form for one of the selected tests."));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familySchemasUsed]);

  // Proove's "selecting a specific profile pre-checks it" rule — now
  // targets the Proove schema's own clinicalDataByFamily bucket
  // specifically, instead of a flat clinicalData object.
  useEffect(() => {
    const toAdd = {};
    selectedTests.forEach((t) => {
      const prefix = TEST_ID_TO_PROFILE_PREFIX[t.test_id];
      if (prefix) toAdd[`${prefix}_selected`] = ["selected"];
    });
    // 2026-10-09, part 2 — same "selecting it in Step 1 pre-checks it in
    // the form" UX, generalized to every OTHER family via the schema-
    // driven map (genericFamilyPreCheckUpdates never clobbers a field the
    // clinician already touched — see its own comment). Proove keeps its
    // own branch above, unchanged.
    const genericUpdates = genericFamilyPreCheckUpdates(selectedTests, schemasByKey, clinicalDataByFamily);
    if (Object.keys(toAdd).length === 0 && Object.keys(genericUpdates).length === 0) return;
    setClinicalDataByFamily((prev) => {
      let next = prev;
      if (Object.keys(toAdd).length > 0) {
        next = { ...next, [PROOVE_PGX_SCHEMA_KEY]: { ...(next[PROOVE_PGX_SCHEMA_KEY] || {}), ...toAdd } };
      }
      Object.entries(genericUpdates).forEach(([schemaKey, fieldUpdates]) => {
        next = { ...next, [schemaKey]: { ...(next[schemaKey] || {}), ...fieldUpdates } };
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTests, schemasByKey]);

  // Drop any family's clinical data once every test belonging to it has
  // been deselected — never submit stale answers for a family the
  // clinician removed from this order.
  useEffect(() => {
    const activeKeys = new Set(familySchemasUsed.map((f) => f.schemaKey));
    setClinicalDataByFamily((prev) => {
      const next = {};
      let changed = false;
      for (const [k, v] of Object.entries(prev)) {
        if (activeKeys.has(k)) next[k] = v;
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [familySchemasUsed]);

  const firstFamilySchema = familySchemasUsed.length ? schemasByKey[familySchemasUsed[0].schemaKey] : null;

  // patient_info's identity fields (first/last/dob/address/phone/email)
  // are locked to the clinician_patient_records row — collected via the
  // custom Step 3 form above, never re-typed here. Its remaining fields
  // (sex/height/weight/race/middle_initial) ARE per-order clinical
  // content, so they're surfaced in Step 4 via a filtered copy of the
  // same section instead of a second, separate schema. Rendered from
  // whichever family's schema loaded first — Part A made this section's
  // content byte-identical across all 3 schemas, so it never matters
  // which one supplies it.
  const patientInfoClinicalSection = useMemo(() => {
    const section = (firstFamilySchema || []).find((s) => s.id === "patient_info");
    if (!section) return null;
    const keep = new Set(["sex", "height_ft", "height_in", "weight_lbs", "race", "middle_initial"]);
    return { ...section, title: "Additional Patient Information", fields: section.fields.filter((f) => keep.has(f.id)) };
  }, [firstFamilySchema]);

  const paymentInfoSection = useMemo(() => {
    return (firstFamilySchema || []).find((s) => s.id === "payment_info") || null;
  }, [firstFamilySchema]);

  // 2026-10-13 fix — real gap confirmed in today's UI/UX sweep:
  // DynamicRequisitionForm's gold asterisk was decorative only — nothing
  // here ever actually checked a `required: true` field before letting a
  // clinician click Next, so Step 4 (Clinical Details) could be clicked
  // through entirely empty and only surface the backend's generic 422 at
  // the very last step. Mirrors the SAME rule the backend's own
  // validate_order_required (order_requisition_schema.py) uses — a flat
  // `field.required` check, no extra conditional rules exist server-side
  // beyond that (confirmed by reading it directly: policy_number/
  // insurance_provider/etc. are NOT `required` even when bill_to ==
  // "insurance") — so this can never block something the server accepts.
  // Computed per-section (not one flat combined schema) because the
  // render below also needs each DynamicRequisitionForm call's own
  // errorFieldIds subset.
  // 2026-10-14 — moved below patientInfoClinicalSection/paymentInfoSection
  // (real bug, not just lint noise: this used to reference both in a
  // dependency array BEFORE either was declared further down this same
  // function body — a genuine Temporal Dead Zone ReferenceError at
  // render time, caught by a real `CI=true npm run build`, not by any
  // bundler/syntax check run earlier).
  //
  // 2026-10-14 fix (confirmed real bug, reported with evidence) — split
  // out paymentMissingFieldIds from sharedSectionMissingFieldIds: the step
  // literally labeled "Patient & billing details" (step 2) never actually
  // rendered any billing field — Payment Information lived in step 3
  // ("Clinical Details") instead, with a stale comment claiming otherwise.
  // Payment Information now renders in step 2 (see the step === 2 JSX
  // below), so its required-field gate must move with it — otherwise step
  // 2 could be passed with required billing fields still empty, and step
  // 3's Next button would stay permanently blocked waiting on fields that
  // no longer render there.
  const paymentMissingFieldIds = useMemo(() => {
    return paymentInfoSection ? validateSchemaRequired([paymentInfoSection], sharedClinicalData) : [];
  }, [paymentInfoSection, sharedClinicalData]);

  const sharedSectionMissingFieldIds = useMemo(() => {
    const ids = [];
    if (patientInfoClinicalSection) ids.push(...validateSchemaRequired([patientInfoClinicalSection], sharedClinicalData));
    return ids;
  }, [patientInfoClinicalSection, sharedClinicalData]);

  const familySectionMissingFieldIds = useMemo(() => {
    const ids = [];
    familySchemasUsed.forEach(({ schemaKey }) => {
      const schema = schemasByKey[schemaKey];
      if (!schema) return; // still loading — nothing to validate yet
      const familySections = schema.filter((s) => !SHARED_SECTION_IDS.includes(s.id));
      ids.push(...validateSchemaRequired(familySections, clinicalDataByFamily[schemaKey] || {}));
    });
    return ids;
  }, [familySchemasUsed, schemasByKey, clinicalDataByFamily]);

  // 2026-10-14 fix — sharedSectionMissingFieldIds/paymentMissingFieldIds
  // no longer count toward step 3; both now render (and gate) in step 2,
  // see canProceedFromStep(2) above.
  const step3MissingFieldCount = familySectionMissingFieldIds.length;

  // Drops whatever THIS feature injected for the previously-selected
  // patient (see appliedPrefillFieldsRef's own comment) and resets the
  // tracking ref — called at the top of both selectPatient and
  // startNewPatient, before anything about the new selection is applied,
  // so a stale prior-patient value can never survive a patient switch.
  const clearAppliedPrefill = () => {
    if (Object.keys(appliedPrefillFieldsRef.current).length > 0) {
      setSharedClinicalData((prev) => stripAppliedPrefill(prev, appliedPrefillFieldsRef.current));
    }
    appliedPrefillFieldsRef.current = {};
  };

  const selectPatient = (p) => {
    clearAppliedPrefill();
    setSelectedPatient(p);
    setIsNewPatient(false);
    // Order a Test, Part 3 — the autofill toggle gates exactly this: does
    // selecting a known patient auto-populate demographics, or leave the
    // form blank for manual re-entry. Confirmed a standing per-clinician
    // preference (Settings), not a per-order control.
    setPatientForm(autofillEnabled ? patientToFormState(p || {}) : patientToFormState({}));
    setLastOrderPrefillNotice(false);

    // 2026-10-10 — billing/demographic autofill now reads PRIMARILY off
    // the patient record ITSELF (already fetched at Step 1/2 — `p` here,
    // no extra round-trip), since clinician_patient_records is now the
    // authoritative, always-current source (kept in sync by every order's
    // two-way sync, and directly editable in My Patients). Only a patient
    // whose own record predates this change (real order history, but the
    // record's own clinical fields are still empty) falls back to the old
    // GET /clinician/patients/{id}/last-order-data endpoint. Only for a
    // RETURNING patient being selected here — a brand-new patient
    // (startNewPatient below) has neither a record nor a prior order to
    // pull from and never triggers either path. Gated behind the SAME
    // autofillEnabled preference as the identity-field prefill above, for
    // one consistent "does selecting a known patient auto-populate
    // things?" toggle.
    if (!p?.id || !autofillEnabled) return;
    const seq = ++lastOrderFetchSeq.current;

    const applyPrefillData = (data) => {
      if (!data || Object.keys(data).length === 0) return;
      setSharedClinicalData((prev) => {
        const { merged, appliedFields } = mergeLastOrderPrefill(prev, data);
        if (Object.keys(appliedFields).length === 0) return prev;
        appliedPrefillFieldsRef.current = { ...appliedPrefillFieldsRef.current, ...appliedFields };
        setLastOrderPrefillNotice(true);
        return merged;
      });
    };

    const { source, data } = patientRecordAutofillSource(p);
    if (source === "record") {
      applyPrefillData(data);
      return;
    }

    // Fallback path — this patient's own record has none of these fields
    // yet (pre-2026-10-10 patient). Race-safety: a sequence counter guards
    // against this response arriving after the clinician has already
    // moved on to a DIFFERENT patient (picked someone else, or started a
    // new patient, before this resolves) — only the most recently issued
    // fetch is allowed to apply its result. Within that still-current
    // response, mergeLastOrderPrefill (inside applyPrefillData) only fills
    // fields that are still empty/unset in sharedClinicalData at the
    // moment it arrives, via a functional setState update (reads the
    // latest state, not a value captured in this closure) — so it never
    // clobbers anything the clinician already typed into Step 3/4 while
    // this request was in flight.
    axios.get(`${API_BASE}/clinician/patients/${p.id}/last-order-data`, { headers: authHeaders })
      .then((res) => {
        if (lastOrderFetchSeq.current !== seq) return; // superseded by a later patient selection
        if (!res.data?.has_prior_order) return;
        applyPrefillData(res.data?.data);
      })
      .catch(() => {}); // best-effort — a failed prefill fetch must never block patient selection
  };

  const startNewPatient = () => {
    clearAppliedPrefill();
    setSelectedPatient(null);
    setIsNewPatient(true);
    setPatientForm(patientToFormState({}));
    setLastOrderPrefillNotice(false);
    lastOrderFetchSeq.current += 1; // supersede any in-flight prior-order fetch
  };

  const resetForNextOrder = (keepPatient) => {
    // When keeping the same patient ("Order Another Test"), land on Select
    // Tests (now step 1) instead of re-showing Select Patient (step 0) —
    // matches the pre-reorder behavior of skipping straight past patient
    // selection when the patient is already known.
    setStep(keepPatient ? 1 : 0); setOrderResult(null); setError("");
    setSelectedTests([]); setFamilyFilter("all");
    setSchemasByKey({}); setSharedClinicalData({}); setClinicalDataByFamily({});
    setProviderConsent(false);
    setIdempotencyKey(null); // a genuinely new order attempt gets its own fresh idempotency key
    setLastOrderPrefillNotice(false);
    appliedPrefillFieldsRef.current = {};
    setSupportingDocRows([emptySupportingDocRow()]);
    setSupportingDocUploadErrors([]);
    if (!keepPatient) { setSelectedPatient(null); setIsNewPatient(false); setPatientForm(patientToFormState({})); }
  };

  // Idempotency-key protection — generate exactly once per order attempt,
  // the first time the clinician reaches Review & Sign. Guarded on
  // `!idempotencyKey` so re-renders of step 4, or stepping back to an
  // earlier step and returning, never regenerate it — same order attempt,
  // same key, for as long as resetForNextOrder hasn't run.
  useEffect(() => {
    if (step === 4 && !idempotencyKey) {
      setIdempotencyKey(generateIdempotencyKey());
    }
  }, [step, idempotencyKey]);

  // 2026-10-22 (Piece 6, ui-ux-checker findings) — shared by both
  // canProceedFromStep's gating AND the field styling below, so a
  // non-blank-but-malformed email (or a too-short phone number) shows
  // the SAME red state that drives the disabled "Next" button, instead
  // of silently blocking with no visible reason (the bug: email styling
  // used to key off blank-only, so "john@gmail" looked valid but still
  // blocked Next with no explanation).
  const patientEmailInvalid = !patientForm.email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(patientForm.email.trim());
  // Mirrors the backend's own check (main.py's _resolve_clinician_patient_
  // record: len(re.sub(r"\D","",normalized_phone)) >= 10) so a too-short
  // number is caught here, not only after the confirm-address checkpoint.
  const patientPhoneInvalid = patientForm.phone.replace(/\D/g, "").length < 10;

  const canProceedFromStep = (s) => {
    if (s === 0) return !!selectedPatient || isNewPatient;
    if (s === 1) return selectedTests.length > 0;
    if (s === 2) {
      // 2026-10-22 (Piece 6, one-identity-one-email invariant) — email
      // and phone are now mandatory (the backend 422s without them).
      return !!(patientForm.first_name.trim() && patientForm.last_name.trim() && patientForm.dob.trim()
        && patientForm.address.line1.trim() && patientForm.address.city.trim() && patientForm.address.state.trim() && patientForm.address.zip.trim()
        && !patientEmailInvalid && !patientPhoneInvalid
        && paymentMissingFieldIds.length === 0 && sharedSectionMissingFieldIds.length === 0);
    }
    // 2026-10-13 fix — Step 4 (Clinical Details, STEPS[3]) used to have no
    // real validation at all (see step3MissingFieldCount's own comment
    // above) — a clinician could click Next through it completely empty.
    // Also requires every family's schema to have actually finished
    // loading first — familySectionMissingFieldIds silently skips a
    // schema that's still in flight (nothing to validate yet), which
    // would otherwise let step3MissingFieldCount read as 0 and briefly
    // reopen the gate for a schema whose required fields haven't even
    // rendered yet.
    if (s === 3) {
      const allSchemasLoaded = familySchemasUsed.every(({ schemaKey }) => !!schemasByKey[schemaKey]);
      return allSchemasLoaded && step3MissingFieldCount === 0;
    }
    return true;
  };

  const next = async () => {
    setError("");
    if (step === 2) {
      // Resolve/save the patient record before moving on, so step 5's
      // submit always has a real clinician_patient_id — matches the
      // confirmed match-on-save rule (same demographics → same record
      // updated in place; changed demographics → a new record).
      // Labarium identity plan — forced confirm checkpoint, right before
      // this save, using the backend's authoritative Smarty validation.
      const confirmedAddress = await confirmAddress(patientForm.address);
      if (!confirmedAddress) return; // "No, let me edit" — stay on this step
      try {
        // 2026-10-11 fix — real gap, reported directly: this call used to
        // send ONLY identity fields, so anything the clinician entered on
        // THIS "Patient & Billing" step (bill_to/policy_number/insurance/
        // sex/height/weight/race/etc.) lived only in local component state
        // (sharedClinicalData) until final order submission's two-way sync
        // — if the order then failed to submit for any reason (e.g. a
        // missing signature), everything typed here was silently lost,
        // never persisted anywhere. Now saved immediately on Continue,
        // independent of whether the order later succeeds. _resolve_
        // clinician_patient_record only touches a field if it's present
        // in the request body (model_fields_set), so omitting a field the
        // clinician hasn't touched yet never clobbers it with blank.
        const clinicalFields = {};
        for (const fid of PATIENT_RECORD_CLINICAL_FIELD_IDS) {
          if (sharedClinicalData[fid] !== undefined) clinicalFields[fid] = sharedClinicalData[fid];
        }
        const res = await axios.post(`${API_BASE}/clinician/patients`, {
          first_name: patientForm.first_name.trim(), last_name: patientForm.last_name.trim(), dob: patientForm.dob,
          address_line1: confirmedAddress.line1.trim(), address_unit: confirmedAddress.unit.trim() || undefined,
          address_city: confirmedAddress.city.trim(), address_state: confirmedAddress.state.trim(), address_zip: confirmedAddress.zip.trim(),
          // 2026-10-22 (Piece 6) — always a real, non-blank value now
          // that canProceedFromStep(2) requires both before Next is even
          // clickable; the `|| undefined` fallback this used to need
          // (when both were optional) is no longer reachable.
          email: patientForm.email.trim(), phone: patientForm.phone.trim(),
          ...clinicalFields,
        }, { headers: authHeaders });
        setSelectedPatient(res.data);
        setPatients((prev) => {
          const exists = prev.some((p) => p.id === res.data.id);
          return exists ? prev.map((p) => (p.id === res.data.id ? res.data : p)) : [res.data, ...prev];
        });
      } catch (err) {
        return setError(extractErrorDetail(err, "Could not save patient details."));
      }
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => setStep((s) => Math.max(s - 1, 0));

  const addSupportingDocRow = () => setSupportingDocRows((rows) => [...rows, emptySupportingDocRow()]);
  const removeSupportingDocRow = (key) => setSupportingDocRows((rows) => rows.filter((r) => r._key !== key));
  const updateSupportingDocRow = (key, patch) =>
    setSupportingDocRows((rows) => rows.map((r) => (r._key === key ? { ...r, ...patch } : r)));

  // Uploads every row that has both a doc type AND a file selected — rows
  // left blank (the default single empty row, for a clinician who adds no
  // supporting documents at all) are silently skipped, never treated as
  // an error. Best-effort per file: one failed upload never blocks the
  // others, and this never re-opens the stepper or touches orderResult/
  // error — the order itself already succeeded by the time this runs.
  // Failed rows (the full row, not just a message) are kept in
  // supportingDocUploadErrors so "Retry failed uploads" on the
  // confirmation card — the clinician's only retry point, since
  // MyPatients.js's own copy of this list is read-only — can resubmit
  // exactly those files without the clinician re-picking them.
  const uploadSupportingDocs = async (orderId, rows) => {
    const rowsToUpload = rows.filter((r) => r.docType && r.file);
    if (rowsToUpload.length === 0) { setSupportingDocUploadErrors([]); return; }
    setUploadingSupportingDocs(true);
    const failed = [];
    for (const row of rowsToUpload) {
      try {
        const formData = new FormData();
        formData.append("doc_type", row.docType);
        if (row.docType === "other" && row.otherLabel) formData.append("doc_type_other_label", row.otherLabel);
        formData.append("files", row.file);
        await axios.post(`${API_BASE}/clinician/orders/${orderId}/supporting-docs`, formData, { headers: authHeaders });
      } catch (err) {
        failed.push({ ...row, lastError: extractErrorDetail(err, "Upload failed.") });
      }
    }
    setSupportingDocUploadErrors(failed);
    setUploadingSupportingDocs(false);
  };

  const retrySupportingDocUploads = () => {
    if (!orderResult?.order_id) return;
    uploadSupportingDocs(orderResult.order_id, supportingDocUploadErrors);
  };

  const handleSubmitOrder = async () => {
    // Double-click guard — belt-and-suspenders on top of the server-side
    // idempotency guarantee, never a replacement for it. A synchronous
    // ref check (rather than relying solely on the `submitting` state,
    // which doesn't actually block a second click fired before React
    // re-renders the disabled Submit button) means two rapid clicks can
    // never fire two requests client-side in the first place.
    if (submittingRef.current) return;

    setError("");
    if (!providerConsent) return setError("Please consent to sign this order with your signature on file.");
    // Bug fix, 2026-10-09 — block client-side, same as the server's own
    // REQUIRE_ONE_OF_PROFILE_FIELDS rule, BEFORE test_ids is derived:
    // families_by_schema on the backend is built from test_ids, so an
    // all-unchecked Proove selection means proove_pgx_v1 is never
    // rendered at all and validate_order_required never runs for it —
    // see orderTestDerivation.js's own comment on
    // proovePresentWithNoProfileChecked for why this can't be left to
    // the server alone.
    if (blockedSelectionMessage) return setError(blockedSelectionMessage);
    // A checked profile that couldn't be resolved to a real test_id
    // (see effectiveSelectedTests) must never be silently dropped —
    // that's the exact failure mode this fix exists to close.
    if (effectiveMissingTestIds.length > 0) {
      return setError(
        "One or more checked profiles could not be matched to an orderable test. Please contact support before placing this order."
      );
    }

    // Should already be set by the step-4 effect above; this fallback
    // only matters if submit is somehow reachable before that effect
    // ran. Never generate a NEW key on a retry — only when one didn't
    // exist at all yet.
    const keyForThisSubmit = idempotencyKey || generateIdempotencyKey();
    if (!idempotencyKey) setIdempotencyKey(keyForThisSubmit);

    submittingRef.current = true;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/clinician/orders`, {
        clinician_patient_id: selectedPatient.id,
        test_ids: effectiveTests.map((t) => t.test_id),
        shared_clinical_data: sharedClinicalData,
        clinical_data: clinicalDataByFamily,
        provider_signature_consent: providerConsent,
        idempotency_key: keyForThisSubmit,
      }, { headers: authHeaders });
      setOrderResult(res.data);
      // Supporting Documents — best-effort, AFTER a confirmed successful
      // order. Wrapped in its own try/catch (on top of
      // uploadSupportingDocs's own per-file try/catch) so nothing from
      // the doc-upload pass can ever reach THIS function's catch below —
      // a failed doc upload must never be mistaken for an
      // ambiguous/failed ORDER submission, which is what that catch
      // block exists to detect.
      try {
        await uploadSupportingDocs(res.data.order_id, supportingDocRows);
      } catch {
        // uploadSupportingDocs already handles per-file failures itself;
        // this is a last-resort guard, not an expected path.
      } finally {
        setUploadingSupportingDocs(false);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Ambiguous — the clinician genuinely cannot know whether the order
      // went through server-side — covers two cases, not just a dropped
      // connection: (1) no `response` at all (network drop, CORS failure,
      // or a client-side timeout if one is ever added), and (2) a gateway-
      // level 5xx (502/504 etc.) whose body isn't our backend's own clean
      // string `detail` — that's a load balancer/proxy timeout page, not a
      // validation failure the backend itself decided on. A clean 4xx, or
      // our own backend's 503s (which only fire AFTER a confirmed rollback
      // — see submit_clinician_order's DB-insert-failure/render-failure
      // paths), always carry a string detail and correctly stay on the
      // explicit-message path below.
      //
      // Critically, do NOT touch idempotencyKey in the ambiguous case, so
      // clicking "Place Order" again reuses the SAME key and is actually
      // safe to retry rather than risking a second order.
      const ambiguous = !err.response || (err.response.status >= 500 && typeof detail !== "string");
      if (ambiguous) {
        setError("We couldn't confirm whether this order went through — click Place Order again to safely check.");
      } else {
        // The backend always sends a plain string detail for this endpoint
        // now (even a multi-family validation failure gets collapsed into
        // one string naming the failing family) — but guard anyway, since
        // rendering a raw object inside <Alert> would crash the page.
        setError(typeof detail === "string" ? detail : "Could not submit this order. Please check your entries and try again.");
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: 14.5, bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#fff",
      "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,53,122,0.14)" },
      "&:hover fieldset": { borderColor: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,53,122,0.3)" },
      "&.Mui-focused fieldset": { borderColor: BRAND.navy, borderWidth: "1.5px" } },
  };

  const familiesPresentInCatalog = useMemo(() => {
    const present = new Set(tests.map((t) => t.family || "proove"));
    return FAMILY_FILTER_ORDER.filter((f) => f === "all" || present.has(f));
  }, [tests]);

  // Sorted by family first so MUI's groupBy never produces a repeated,
  // non-contiguous group header for the same family.
  const sortedTests = useMemo(() => {
    return [...tests].sort((a, b) => (a.family || "").localeCompare(b.family || "") || a.display_name.localeCompare(b.display_name));
  }, [tests]);

  // The family chip row narrows which options are VISIBLE in the
  // dropdown without ever touching `options` itself (wrapping MUI's own
  // default filter rather than swapping the options array) — so a test
  // already selected from a different family stays a valid, renderable
  // chip even while the dropdown is filtered to a different family.
  const filterOptions = (options, state) => {
    const base = baseAutocompleteFilter(options, state);
    if (familyFilter === "all") return base;
    return base.filter((t) => (t.family || "proove") === familyFilter);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: isDark ? "#080d1a" : "#f8f6f2" }}>
      {addressConfirmDialog}
      <ClinicianPageHeader
        title="Order a Test"
        subtitle="Select or add a patient, select one or more tests, complete the requisition(s), and sign — delivered directly to the lab."
      />

      <Box sx={{ maxWidth: 860, mx: "auto", px: 3, py: 5 }}>
        <AnimatePresence mode="wait">
          {orderResult ? (
            <motion.div key="confirmation" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              <Card sx={{
                borderRadius: 4, p: 5, textAlign: "center",
                border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,53,122,0.1)",
                bgcolor: isDark ? "#111827" : "#fff",
                boxShadow: isDark ? "0 4px 28px rgba(0,0,0,0.35)" : "0 4px 28px rgba(0,53,122,0.08)",
              }}>
                <CheckCircle sx={{ fontSize: 56, color: "#22c55e", mb: 2 }} />
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, mb: 1 }}>Order Submitted</Typography>
                <Typography sx={{ color: (isDark ? "#94a3b8" : "#64748b"), mb: 1 }}>
                  {effectiveTestNamesSummary} ordered for {patientForm.first_name} {patientForm.last_name}.
                </Typography>
                {orderResult.requisitions && orderResult.requisitions.length > 1 && (
                  <Typography sx={{ color: (isDark ? "#94a3b8" : "#64748b"), mb: 3, fontSize: 13 }}>
                    {orderResult.requisitions.length} separate requisitions were generated (one per test family).
                  </Typography>
                )}
                {uploadingSupportingDocs && (
                  <Alert severity="info" sx={{ mb: 2, borderRadius: 2, textAlign: "left" }}>
                    Uploading supporting document(s)…
                  </Alert>
                )}
                {supportingDocUploadErrors.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, textAlign: "left" }}
                    action={
                      <Button size="small" onClick={retrySupportingDocUploads} disabled={uploadingSupportingDocs}
                        sx={{ textTransform: "none", fontWeight: 700 }}>
                        Retry
                      </Button>
                    }>
                    The order was placed successfully, but {supportingDocUploadErrors.length === 1 ? "a supporting document" : "some supporting documents"} could
                    not be uploaded: {supportingDocUploadErrors.map((r) => `${SUPPORTING_DOC_TYPE_OPTIONS.find((o) => o.value === r.docType)?.label || r.docType} (${r.lastError})`).join("; ")}.
                  </Alert>
                )}
                <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", mt: 2 }}>
                  <Button variant="outlined" onClick={() => navigate(`/my-patients/${selectedPatient.id}`)}
                    sx={{ textTransform: "none", borderColor: isDark ? BRAND.gold : BRAND.navy, color: isDark ? BRAND.gold : BRAND.navy }}>
                    View in My Patients
                  </Button>
                  <Button variant="contained" onClick={() => resetForNextOrder(true)}
                    sx={{ textTransform: "none", bgcolor: BRAND.navy, "&:hover": { bgcolor: BRAND.navyDark } }}>
                    Order Another Test
                  </Button>
                </Box>
              </Card>
            </motion.div>
          ) : (
            <motion.div key="stepper" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Box sx={{
                mb: 4, p: { xs: 2, sm: 2.5 }, borderRadius: 3, bgcolor: isDark ? "#111827" : "#fff",
                border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,53,122,0.1)",
                boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,53,122,0.06)",
              }}>
                <Stepper
                  activeStep={step}
                  alternativeLabel
                  sx={{
                    "& .MuiStepLabel-label": {
                      fontSize: 12.5, fontWeight: 600, color: (isDark ? "#94a3b8" : "#64748b"), mt: 0.75,
                      display: { xs: "none", sm: "block" },
                    },
                    "& .MuiStepLabel-label.Mui-active": { color: isDark ? "#f1f5f9" : BRAND.navy, fontWeight: 800 },
                    "& .MuiStepLabel-label.Mui-completed": { color: isDark ? BRAND.goldLight : BRAND.navyMid, fontWeight: 700 },
                    "& .MuiStepIcon-root": { color: isDark ? "rgba(255,255,255,0.14)" : "rgba(0,53,122,0.14)" },
                    "& .MuiStepIcon-root.Mui-active": { color: isDark ? BRAND.gold : BRAND.navy },
                    "& .MuiStepIcon-root.Mui-completed": { color: BRAND.gold },
                    "& .MuiStepConnector-line": { borderColor: isDark ? "rgba(255,255,255,0.14)" : "rgba(0,53,122,0.14)" },
                    "& .MuiStepConnector-root.Mui-active .MuiStepConnector-line, & .MuiStepConnector-root.Mui-completed .MuiStepConnector-line": {
                      borderColor: BRAND.gold,
                    },
                  }}
                >
                  {STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
                </Stepper>
                <Typography sx={{
                  display: { xs: "block", sm: "none" }, textAlign: "center", mt: 1.5,
                  fontSize: 13, fontWeight: 700, color: isDark ? "#f1f5f9" : BRAND.navy,
                }}>
                  Step {step + 1} of {STEPS.length} · {STEPS[step]}
                </Typography>
              </Box>

              {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError("")}>{error}</Alert>}

              <Card sx={{
                borderRadius: 4, p: { xs: 2.5, sm: 4 },
                border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,53,122,0.1)",
                bgcolor: isDark ? "#111827" : "#fff",
                boxShadow: isDark ? "0 4px 28px rgba(0,0,0,0.35)" : "0 4px 28px rgba(0,53,122,0.07)",
                overflow: "hidden", position: "relative",
              }}>
                <Box sx={{
                  position: "absolute", top: 0, left: 0, right: 0, height: 3,
                  background: `linear-gradient(to right, ${BRAND.navy}, ${BRAND.navyMid}, ${BRAND.gold})`,
                }} />
                {step === 1 && (
                  <Box>
                    <Typography sx={{ fontSize: 17, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, mb: 0.5 }}>Select tests</Typography>
                    <Typography sx={{ fontSize: 14, color: (isDark ? "#94a3b8" : "#64748b"), mb: 2.5 }}>
                      Select one or more Labarium tests to order. Tests from the same family (e.g. 2 Proove profiles)
                      produce a single shared requisition; different families each get their own.
                    </Typography>

                    {familiesPresentInCatalog.length > 1 && (
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2.5 }}>
                        {familiesPresentInCatalog.map((f) => (
                          <Chip key={f} label={f === "all" ? "All" : (FAMILY_LABELS[f] || f)}
                            onClick={() => setFamilyFilter(f)}
                            variant={familyFilter === f ? "filled" : "outlined"}
                            sx={{
                              borderColor: isDark ? BRAND.gold : BRAND.navy, fontWeight: 600, cursor: "pointer",
                              color: familyFilter === f ? "#fff" : (isDark ? BRAND.gold : BRAND.navy),
                              bgcolor: familyFilter === f ? BRAND.navy : "transparent",
                              "&:hover": { bgcolor: familyFilter === f ? BRAND.navyDark : (isDark ? "rgba(255,255,255,0.06)" : "rgba(0,53,122,0.06)") },
                            }} />
                        ))}
                      </Box>
                    )}

                    {recommendedTestIds.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: (isDark ? "#94a3b8" : "#64748b"), textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>Recommended</Typography>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          {tests.filter((t) => recommendedTestIds.includes(t.test_id)).map((t) => {
                            const isSelected = selectedTests.some((s) => s.test_id === t.test_id);
                            return (
                              <Chip key={t.test_id} icon={<Science sx={{ fontSize: 16 }} />} label={t.display_name}
                                onClick={() => setSelectedTests((prev) => (
                                  isSelected ? prev.filter((s) => s.test_id !== t.test_id) : [...prev, t]
                                ))}
                                variant={isSelected ? "filled" : "outlined"}
                                sx={{ borderColor: isDark ? BRAND.gold : BRAND.navy, color: isSelected ? "#fff" : (isDark ? BRAND.gold : BRAND.navy),
                                  bgcolor: isSelected ? BRAND.navy : "transparent", cursor: "pointer", fontWeight: 600,
                                  "&:hover": { bgcolor: isSelected ? BRAND.navyDark : (isDark ? "rgba(255,255,255,0.06)" : "rgba(0,53,122,0.06)") } }} />
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                    <Autocomplete
                      multiple
                      options={sortedTests} value={selectedTests}
                      groupBy={(t) => FAMILY_LABELS[t.family] || t.family || "Other"}
                      filterOptions={filterOptions}
                      getOptionLabel={(t) => t.display_name || ""}
                      isOptionEqualToValue={(a, b) => a.test_id === b.test_id}
                      onChange={(e, v) => setSelectedTests(v)}
                      // The field itself keeps the search icon as its only
                      // adornment — selected tests render as our own chip
                      // list below (see right after this Autocomplete),
                      // not MUI's default inline tags, so they have room
                      // to show their family accent and stay legible
                      // alongside the group-filtered dropdown.
                      renderTags={() => null}
                      renderGroup={(params) => {
                        const key = FAMILY_LABEL_TO_KEY[params.group];
                        const { accent, textAccent, icon: Icon } = familyMeta(key, isDark);
                        return (
                          <li key={params.key}>
                            <Box sx={{
                              display: "flex", alignItems: "center", gap: 0.75,
                              px: 1.75, py: 0.85, position: "sticky", top: -8, zIndex: 1,
                              bgcolor: isDark ? "#0d1420" : "#f8f6f2", borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.08)",
                            }}>
                              <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: accent, flexShrink: 0 }} />
                              <Icon sx={{ fontSize: 15, color: textAccent }} />
                              <Typography sx={{ fontSize: 12, fontWeight: 800, color: textAccent, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                {params.group}
                              </Typography>
                            </Box>
                            <Box component="ul" sx={{ p: 0, m: 0 }}>{params.children}</Box>
                          </li>
                        );
                      }}
                      slotProps={{ paper: { sx: { borderRadius: "12px", mt: 0.5, boxShadow: "0 12px 36px rgba(0,53,122,0.15)" } } }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          hiddenLabel
                          placeholder={selectedTests.length ? "Add another test…" : "Search tests…"}
                          sx={{ ...inputSx, "& .MuiOutlinedInput-root": { ...inputSx["& .MuiOutlinedInput-root"], py: 0.5 } }}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: <Search sx={{ fontSize: 19, color: (isDark ? "#94a3b8" : "#64748b"), ml: 0.5, mr: -0.5 }} />,
                          }}
                        />
                      )}
                    />
                    {selectedTests.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: (isDark ? "#94a3b8" : "#64748b"), textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
                          Selected ({selectedTests.length})
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          {selectedTests.map((t) => {
                            const { textAccent, icon: Icon } = familyMeta(t.family, isDark);
                            return (
                              <Chip
                                key={t.test_id}
                                icon={<Icon sx={{ fontSize: 16, color: `${textAccent} !important` }} />}
                                label={t.display_name}
                                onDelete={() => setSelectedTests((prev) => prev.filter((s) => s.test_id !== t.test_id))}
                                deleteIcon={<Close sx={{ fontSize: 16 }} />}
                                sx={{
                                  height: 34, fontSize: 13.5, fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b",
                                  bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#fff", border: `1.5px solid ${textAccent}`,
                                  "& .MuiChip-deleteIcon": { color: (isDark ? "#94a3b8" : "#64748b"), "&:hover": { color: textAccent } },
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {step === 0 && (
                  <Box>
                    <Typography sx={{ fontSize: 17, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, mb: 0.5 }}>Select a patient</Typography>
                    <Typography sx={{ fontSize: 14, color: (isDark ? "#94a3b8" : "#64748b"), mb: 2.5 }}>Select an existing patient, or add a new one.</Typography>
                    <Button variant={isNewPatient ? "contained" : "outlined"} onClick={startNewPatient}
                      sx={{ mb: 2, textTransform: "none", fontWeight: 600, borderRadius: "10px",
                        bgcolor: isNewPatient ? BRAND.navy : "transparent",
                        borderColor: isDark ? BRAND.gold : BRAND.navy, color: isNewPatient ? "#fff" : (isDark ? BRAND.gold : BRAND.navy),
                        "&:hover": { bgcolor: isNewPatient ? BRAND.navyDark : (isDark ? "rgba(255,255,255,0.06)" : "rgba(0,53,122,0.06)"), borderColor: isDark ? BRAND.gold : BRAND.navy } }}>
                      + New Patient
                    </Button>
                    <Autocomplete
                      options={patients} value={isNewPatient ? null : selectedPatient}
                      getOptionLabel={(p) => `${p.first_name} ${p.last_name} — DOB ${p.dob}`}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      onChange={(e, v) => (v ? selectPatient(v) : setSelectedPatient(null))}
                      slotProps={{ paper: { sx: { borderRadius: "12px", mt: 0.5, boxShadow: "0 12px 36px rgba(0,53,122,0.15)" } } }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          hiddenLabel
                          placeholder="Search by name or date of birth…"
                          sx={{ ...inputSx, "& .MuiOutlinedInput-root": { ...inputSx["& .MuiOutlinedInput-root"], py: 0.5 } }}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: <Search sx={{ fontSize: 19, color: (isDark ? "#94a3b8" : "#64748b"), ml: 0.5, mr: -0.5 }} />,
                          }}
                        />
                      )}
                    />
                    {patientsTruncated && (
                      <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2 }}>
                        Showing the first 500 patients — search by name or date of birth to find someone not listed here.
                      </Alert>
                    )}
                    {patientsLoadError && (
                      <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }} action={
                        <Button size="small" onClick={loadPatients} sx={{ textTransform: "none" }}>Retry</Button>
                      }>
                        Couldn't load your patient list. This is a real error, not an empty result.
                      </Alert>
                    )}
                  </Box>
                )}

                {step === 2 && (
                  <Box>
                    <Typography sx={{ fontSize: 17, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, mb: 0.5 }}>Patient & billing details</Typography>
                    <Typography sx={{ fontSize: 14, color: (isDark ? "#94a3b8" : "#64748b"), mb: 2.5 }}>
                      These details are saved to this patient's record and reused next time you order for them.
                    </Typography>
                    {/* 2026-10-13 fix — real gap confirmed in today's UI/UX
                        sweep: these fields ARE enforced by
                        canProceedFromStep (first/last/dob/address), but
                        this step showed NO asterisks at all — the exact
                        inverse of DynamicRequisitionForm's bug (decorative
                        asterisk, no enforcement). Same gold-asterisk-
                        normally/red-when-missing convention as every other
                        required field in the app now. */}
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, mb: 0.4 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: !patientForm.first_name.trim() ? "#dc2626" : (isDark ? "#94a3b8" : "#64748b") }}>
                          First name<Box component="span" sx={{ color: !patientForm.first_name.trim() ? "#dc2626" : BRAND.gold, ml: 0.3 }}>*</Box>
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: !patientForm.last_name.trim() ? "#dc2626" : (isDark ? "#94a3b8" : "#64748b") }}>
                          Last name<Box component="span" sx={{ color: !patientForm.last_name.trim() ? "#dc2626" : BRAND.gold, ml: 0.3 }}>*</Box>
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, mb: 2 }}>
                      <TextField hiddenLabel fullWidth placeholder="First name"
                        sx={{ ...inputSx, ...(!patientForm.first_name.trim() ? { "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#dc2626 !important", borderWidth: "1.5px" } } } : {}) }}
                        value={patientForm.first_name} onChange={(e) => setPatientForm((p) => ({ ...p, first_name: e.target.value }))} />
                      <TextField hiddenLabel fullWidth placeholder="Last name"
                        sx={{ ...inputSx, ...(!patientForm.last_name.trim() ? { "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#dc2626 !important", borderWidth: "1.5px" } } } : {}) }}
                        value={patientForm.last_name} onChange={(e) => setPatientForm((p) => ({ ...p, last_name: e.target.value }))} />
                    </Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: !patientForm.dob.trim() ? "#dc2626" : (isDark ? "#94a3b8" : "#64748b"), mb: 0.4 }}>
                      Date of birth<Box component="span" sx={{ color: !patientForm.dob.trim() ? "#dc2626" : BRAND.gold, ml: 0.3 }}>*</Box>
                    </Typography>
                    <TextField hiddenLabel fullWidth type="date"
                      sx={{ mb: 2, maxWidth: 220, ...inputSx, ...(!patientForm.dob.trim() ? { "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#dc2626 !important", borderWidth: "1.5px" } } } : {}) }}
                      value={patientForm.dob} onChange={(e) => setPatientForm((p) => ({ ...p, dob: e.target.value }))}
                      slotProps={{ inputLabel: { shrink: true } }} />
                    <AddressFields isDark={isDark} inputSx={inputSx} value={patientForm.address} showRequired
                      onChange={(addr) => setPatientForm((p) => ({ ...p, address: addr }))}
                      helperText="If this patient already has an address on file, it's pre-filled above. Otherwise, enter their current address — this helps us match them to their lab results." />
                    {/* 2026-10-22 (Piece 6, one-identity-one-email
                        invariant) — both are now mandatory (the backend
                        422s without them, same reasoning as patient
                        registration/report generation/Proove upload:
                        resolve_identity's own identity-claim check needs
                        a real email, and the record couldn't otherwise
                        ever be claimed at all). Same gold-asterisk-
                        normally/red-when-missing convention as the
                        identity fields above. */}
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, mb: 0.4, mt: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: patientEmailInvalid ? "#dc2626" : (isDark ? "#94a3b8" : "#64748b") }}>
                          Email<Box component="span" sx={{ color: patientEmailInvalid ? "#dc2626" : BRAND.gold, ml: 0.3 }}>*</Box>
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: patientPhoneInvalid ? "#dc2626" : (isDark ? "#94a3b8" : "#64748b") }}>
                          Phone<Box component="span" sx={{ color: patientPhoneInvalid ? "#dc2626" : BRAND.gold, ml: 0.3 }}>*</Box>
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, mb: 2 }}>
                      <TextField hiddenLabel fullWidth type="email" placeholder="Email"
                        sx={{ ...inputSx, ...(patientEmailInvalid ? { "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#dc2626 !important", borderWidth: "1.5px" } } } : {}) }}
                        value={patientForm.email} onChange={(e) => setPatientForm((p) => ({ ...p, email: e.target.value }))} />
                      <TextField hiddenLabel fullWidth type="tel" placeholder="Phone"
                        sx={{ ...inputSx, ...(patientPhoneInvalid ? { "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#dc2626 !important", borderWidth: "1.5px" } } } : {}) }}
                        value={patientForm.phone} onChange={(e) => setPatientForm((p) => ({ ...p, phone: e.target.value }))} />
                    </Box>
                    {/* 2026-10-22 (ui-ux-checker finding) — an existing
                        patient selected from a pre-Piece-6 record may have
                        blank email/phone on file; without this, the red
                        asterisk/border above is the only cue, easy to miss
                        on a step with several other fields. */}
                    {!!selectedPatient && (patientEmailInvalid || patientPhoneInvalid) && (
                      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                        We need a valid email and phone number on file for this patient before you can continue — please add them above.
                      </Alert>
                    )}

                    {/* 2026-10-14 fix — real bug, reported with evidence: this
                        step is literally labeled "Patient & billing details"
                        but never rendered a single billing field — Payment
                        Information lived in the NEXT step ("Clinical
                        Details") instead, with a stale comment claiming it
                        was here. Moved the actual render here so the label
                        matches reality; required-field gating moved with it
                        (see paymentMissingFieldIds / canProceedFromStep). */}
                    {/* 2026-10-14 fix (user's own explicit instruction,
                        not a label-only fix) — "Additional Patient
                        Information" (sex/height/weight/race) and Billing
                        both now live here, in the SAME shared autofilling
                        patient-info section as name/DOB/address, instead
                        of Additional Patient Information being split off
                        into the later Clinical Details step. */}
                    {(patientInfoClinicalSection?.fields.length > 0) && (() => {
                      const missingIds = sharedSectionMissingFieldIds;
                      return (
                        <Box sx={{ mt: 3 }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 800, color: (isDark ? "#94a3b8" : "#64748b"), textTransform: "uppercase", letterSpacing: "0.07em", mb: 1 }}>
                            Additional Patient Information{missingIds.length > 0 ? ` — ${missingIds.length} required` : ""}
                          </Typography>
                          {lastOrderPrefillNotice && (
                            <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2, fontSize: 13 }} onClose={() => setLastOrderPrefillNotice(false)}>
                              Pre-filled from this patient's record — please review before continuing.
                            </Alert>
                          )}
                          <DynamicRequisitionForm schema={[patientInfoClinicalSection]} data={sharedClinicalData} onChange={setSharedClinicalData} isDark={isDark}
                            errorFieldIds={missingIds} />
                        </Box>
                      );
                    })()}

                    {paymentInfoSection && (() => {
                      return (
                        <Box sx={{ mt: 3 }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 800, color: (isDark ? "#94a3b8" : "#64748b"), textTransform: "uppercase", letterSpacing: "0.07em", mb: 1 }}>
                            Billing Information
                          </Typography>
                          <DynamicRequisitionForm schema={[paymentInfoSection]} data={sharedClinicalData} onChange={setSharedClinicalData} isDark={isDark}
                            errorFieldIds={paymentMissingFieldIds} />
                        </Box>
                      );
                    })()}
                  </Box>
                )}

                {step === 3 && (
                  familySchemasUsed.length === 0 ? (
                    <Typography sx={{ color: (isDark ? "#94a3b8" : "#64748b"), fontSize: 14 }}>No tests selected.</Typography>
                  ) : familySchemasUsed.some(({ schemaKey }) => !schemasByKey[schemaKey]) ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress sx={{ color: BRAND.navy }} /></Box>
                  ) : (
                    <Box>
                      <Typography sx={{ fontSize: 17, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, mb: 2 }}>Clinical details</Typography>

                      {/* One clearly-labeled section per DISTINCT family
                          actually selected — generic over the schema's
                          own section list (never a hardcoded Proove-only
                          section-id list), so Toxicology/Infectious
                          Disease render their own real content instead of
                          nothing. Each card gets its family's accent
                          color/icon (see FAMILY_META) so several stacked
                          sections are distinguishable at a glance, plus
                          real breathing room between them (mb: 4, vs. the
                          previous mb: 3 that read as cramped). */}
                      {familySchemasUsed.length > 0 && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
                          <Receipt sx={{ fontSize: 16, color: (isDark ? "#94a3b8" : "#64748b") }} />
                          <Typography sx={{ fontSize: 12, fontWeight: 800, color: (isDark ? "#94a3b8" : "#64748b"), textTransform: "uppercase", letterSpacing: "0.07em" }}>
                            Family-Specific Details
                          </Typography>
                        </Box>
                      )}
                      {familySchemasUsed.map(({ schemaKey, family }) => {
                        const schema = schemasByKey[schemaKey] || [];
                        const familySections = schema.filter((s) => !SHARED_SECTION_IDS.includes(s.id));
                        if (familySections.length === 0) return null;
                        const { accent, textAccent, icon: Icon } = familyMeta(family, isDark);
                        return (
                          <Box key={schemaKey} sx={{
                            mb: 4, borderRadius: 3, border: `1px solid ${accent}33`,
                            bgcolor: isDark ? "#0d1420" : "#fcfbf8", overflow: "hidden",
                          }}>
                            <Box sx={{
                              display: "flex", alignItems: "center", gap: 1.25,
                              px: { xs: 1.75, sm: 2.5 }, py: 1.5, bgcolor: `${accent}14`,
                              borderBottom: `1px solid ${accent}33`,
                            }}>
                              <Box sx={{
                                width: 30, height: 30, borderRadius: "9px", bgcolor: accent,
                                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                              }}>
                                <Icon sx={{ fontSize: 17, color: "#fff" }} />
                              </Box>
                              <Typography sx={{ fontSize: 15, fontWeight: 800, color: textAccent }}>
                                {FAMILY_LABELS[family] || family} — Requisition Details
                              </Typography>
                            </Box>
                            <Box sx={{ px: { xs: 1.75, sm: 2.5 }, pt: 2, pb: 2.5 }}>
                              {familySections.map((section) => {
                                const missingIds = validateSchemaRequired([section], clinicalDataByFamily[schemaKey] || {});
                                return (
                                // 2026-10-13 fix — same "missing required field hidden
                                // inside a collapsed accordion" gap as the shared-section
                                // accordions above — auto-expand whenever this section
                                // has something missing, plus a red title + count.
                                <Accordion key={section.id} defaultExpanded={familySections.length === 1 || missingIds.length > 0} sx={{ ...accordionSx(isDark), "&:last-of-type": { mb: 0 } }}>
                                  <AccordionSummary expandIcon={<ExpandMore sx={{ color: missingIds.length > 0 ? "#dc2626" : (isDark ? BRAND.gold : BRAND.navy) }} />}
                                    sx={{ bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,53,122,0.03)" }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: missingIds.length > 0 ? "#dc2626" : (isDark ? BRAND.gold : BRAND.navy) }}>
                                      {section.title}{missingIds.length > 0 ? ` — ${missingIds.length} required` : ""}
                                    </Typography>
                                  </AccordionSummary>
                                  <AccordionDetails sx={{ bgcolor: isDark ? "#111827" : "#fff" }}>
                                    <DynamicRequisitionForm
                                      schema={[section]}
                                      data={clinicalDataByFamily[schemaKey] || {}}
                                      onChange={(d) => setClinicalDataByFamily((prev) => ({ ...prev, [schemaKey]: d }))}
                                      isDark={isDark}
                                      errorFieldIds={missingIds}
                                    />
                                  </AccordionDetails>
                                </Accordion>
                                );
                              })}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )
                )}

                {step === 4 && (
                  <Box>
                    <Typography sx={{ fontSize: 17, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, mb: 1.5 }}>Review & sign</Typography>
                    <Box sx={{
                      borderRadius: "12px", border: "1px solid rgba(0,53,122,0.1)", bgcolor: "rgba(0,53,122,0.025)",
                      px: 2, py: 1.5, mb: 1,
                    }}>
                      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", py: 0.5, gap: 1 }}>
                        <Typography sx={{ fontSize: 13, color: (isDark ? "#94a3b8" : "#64748b"), fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", pt: 0.25 }}>Tests</Typography>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b", textAlign: "right" }}>{effectiveTestNamesSummary}
                          <Button size="small" onClick={() => setStep(1)} sx={{ ml: 1, textTransform: "none", fontSize: 12, color: isDark ? BRAND.gold : BRAND.navy }}>Edit</Button>
                        </Typography>
                      </Box>
                      <Box sx={{ height: "1px", bgcolor: "rgba(0,53,122,0.08)" }} />
                      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", py: 0.5, gap: 1 }}>
                        <Typography sx={{ fontSize: 13, color: (isDark ? "#94a3b8" : "#64748b"), fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Patient</Typography>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b" }}>{patientForm.first_name} {patientForm.last_name} (DOB {patientForm.dob})
                          <Button size="small" onClick={() => setStep(2)} sx={{ ml: 1, textTransform: "none", fontSize: 12, color: isDark ? BRAND.gold : BRAND.navy }}>Edit</Button>
                        </Typography>
                      </Box>
                    </Box>

                    {/* Multi-requisition summary — a scannable row per
                        distinct family instead of one buried sentence,
                        matching the accent/icon language established in
                        Step 3. Shown even for a single family so Review &
                        Sign never has an abrupt layout jump depending on
                        how many requisitions this order happens to carry. */}
                    <Box sx={{ mt: 2, mb: 1 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 800, color: (isDark ? "#94a3b8" : "#64748b"), textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
                        {effectiveFamilySchemasUsed.length} requisition{effectiveFamilySchemasUsed.length > 1 ? "s" : ""} will be generated
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {effectiveFamilySchemasUsed.map(({ schemaKey, family, testIds }) => {
                          const { accent, textAccent, icon: Icon } = familyMeta(family, isDark);
                          const names = effectiveTests.filter((t) => testIds.includes(t.test_id)).map((t) => t.display_name).join(", ");
                          // Fed straight into POST /clinician/orders/preview-requisition-pdf
                          // — server derives identity fields from
                          // clinician_patient_id itself (never trusts
                          // these for identity, same anti-spoof rule as
                          // real submission), so this only needs to carry
                          // the clinical content actually being previewed.
                          const previewParams = {
                            clinician_patient_id: selectedPatient?.id,
                            schema_key: schemaKey,
                            shared_clinical_data: sharedClinicalData,
                            clinical_data: clinicalDataByFamily,
                          };
                          // Recomputed on every render from the exact data
                          // that feeds the rendered PDF for THIS family —
                          // compared against the toggle's own "what did I
                          // last fetch" ref so stepping back and editing
                          // Step 3/4, then returning here, always shows the
                          // real current content instead of a replayed
                          // stale preview.
                          const previewDepsSignature = JSON.stringify({
                            patientId: selectedPatient?.id, schemaKey,
                            shared: sharedClinicalData, family: clinicalDataByFamily[schemaKey],
                          });
                          return (
                            <Box key={schemaKey} sx={{
                              display: "flex", flexDirection: "column", gap: 0.5, px: 1.75, py: 1.1,
                              borderRadius: "10px", border: `1px solid ${accent}33`, bgcolor: `${accent}0d`,
                            }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                                <Box sx={{
                                  width: 26, height: 26, borderRadius: "8px", bgcolor: accent, flexShrink: 0,
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                }}>
                                  <Icon sx={{ fontSize: 15, color: "#fff" }} />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: textAccent }}>
                                    {FAMILY_LABELS[family] || family}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12.5, color: (isDark ? "#94a3b8" : "#64748b") }}>{names}</Typography>
                                </Box>
                              </Box>
                              {/* 2026-10-13 fix — the real gap this whole
                                  piece exists to close: before this, a
                                  clinician signed this requisition without
                                  ever seeing the actual rendered PDF they
                                  were attesting to, only this HTML summary
                                  row. No persisted GCS blob exists yet
                                  pre-submission, so this renders on the fly
                                  from the current in-progress form data via
                                  the preview-only backend call — see
                                  RequisitionPreviewToggle's own comment. */}
                              <RequisitionPreviewToggle
                                title={FAMILY_LABELS[family] || family}
                                schemaKey={schemaKey}
                                previewParams={previewParams}
                                depsSignature={previewDepsSignature}
                                authHeaders={authHeaders}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>

                    {/* Bug fix, 2026-10-09 — surfaced here, not just as a
                        submit-time error, so the clinician sees it while
                        still on Review (where the mismatch between what
                        was checked in the form and what's about to be
                        ordered is actually visible/fixable) rather than
                        only after clicking Place Order. */}
                    {blockedSelectionMessage && (
                      <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{blockedSelectionMessage}</Alert>
                    )}
                    {effectiveMissingTestIds.length > 0 && (
                      <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        One or more checked profiles could not be matched to an orderable test. Please contact support before placing this order.
                      </Alert>
                    )}

                    <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1, mt: 3 }}>Ordering Provider Signature</Typography>
                    <Typography sx={{ fontSize: 13.5, color: (isDark ? "#94a3b8" : "#64748b"), mb: 1 }}>
                      Your signature on file with Labarium will be used to sign {effectiveFamilySchemasUsed.length > 1 ? "each requisition in" : ""} this order
                      — you don't need to re-draw it for every order. You can update your signature anytime in Settings.
                    </Typography>
                    <FormControlLabel
                      sx={{ alignItems: "flex-start", mt: 1 }}
                      control={<Checkbox checked={providerConsent} onChange={(e) => setProviderConsent(e.target.checked)}
                        sx={{ color: isDark ? "rgba(255,255,255,0.4)" : BRAND.navy, mt: -0.5, "&.Mui-checked": { color: isDark ? BRAND.gold : BRAND.navy } }} />}
                      label={<Typography sx={{ fontSize: 13, color: (isDark ? "#94a3b8" : "#64748b"), lineHeight: 1.6 }}>
                        By checking this box, I am electronically signing this order using my signature on file with Labarium.
                        I certify that I am a licensed medical professional authorized to order these services, that I have
                        determined they are medically necessary for this patient, and that I am not receiving any financial
                        inducement to order them.
                      </Typography>}
                    />

                    <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1, mt: 3 }}>Patient Consent &amp; Signature</Typography>
                    {/* 2026-10-08 — in-office patient signing removed: the
                        patient now always consents and signs on their own
                        async signing page (no login required), not here.
                        The Patient Consents and Authorizations checkboxes
                        and the patient SignaturePad that used to live here
                        both moved to PatientConsent.js, verbatim — not
                        duplicated in both places. This step just sets the
                        clinician's expectation for what happens next. */}
                    {/* 2026-10-08 — item 2/3 fix: the signing link now goes
                        out over whichever of email/phone the patient has
                        on file (both, if both) — see
                        _issue_patient_consent_link in main.py. Order
                        submission itself is blocked with a 422 when NEITHER
                        exists (see submit_clinician_order), so this box no
                        longer has a true "nothing will happen" case to
                        describe — it only ever says WHICH channel(s). */}
                    {selectedPatient?.email && selectedPatient?.phone ? (
                      <Box sx={{
                        p: 1.75, borderRadius: 2, bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,53,122,0.05)",
                        border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,53,122,0.15)",
                      }}>
                        <Typography sx={{ fontSize: 13.5, color: isDark ? "#e2e8f0" : "#1e293b", lineHeight: 1.6 }}>
                          A secure signing link will be sent to the patient by email at <strong>{selectedPatient.email}</strong> and
                          by text at <strong>{selectedPatient.phone}</strong> after you submit this order. They'll review the
                          order, give their own consent, and sign — no login required.
                        </Typography>
                      </Box>
                    ) : selectedPatient?.email ? (
                      <Box sx={{
                        p: 1.75, borderRadius: 2, bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,53,122,0.05)",
                        border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,53,122,0.15)",
                      }}>
                        <Typography sx={{ fontSize: 13.5, color: isDark ? "#e2e8f0" : "#1e293b", lineHeight: 1.6 }}>
                          A secure signing link will be sent to the patient by email at <strong>{selectedPatient.email}</strong> after
                          you submit this order. They'll review the order, give their own consent, and sign — no
                          login required.
                        </Typography>
                      </Box>
                    ) : selectedPatient?.phone ? (
                      <Box sx={{
                        p: 1.75, borderRadius: 2, bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,53,122,0.05)",
                        border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,53,122,0.15)",
                      }}>
                        <Typography sx={{ fontSize: 13.5, color: isDark ? "#e2e8f0" : "#1e293b", lineHeight: 1.6 }}>
                          A secure signing link will be sent to the patient by text at <strong>{selectedPatient.phone}</strong> after
                          you submit this order. They'll review the order, give their own consent, and sign — no
                          login required.
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{
                        p: 1.75, borderRadius: 2, bgcolor: "rgba(220,38,38,0.08)",
                        border: "1px solid rgba(220,38,38,0.3)",
                      }}>
                        <Typography sx={{ fontSize: 13.5, color: isDark ? "#e2e8f0" : "#1e293b", lineHeight: 1.6 }}>
                          {/* 2026-10-13 fix — real bug confirmed in today's UI/UX
                              sweep: this referenced the internal 0-indexed step
                              number (`step === 2`, "Patient & Billing") instead of
                              the human-facing 1-indexed label the Stepper itself
                              shows ("Step 3 of 5" — see STEPS[2] above). */}
                          <strong>No email or phone number on file for this patient.</strong> Add at least one in
                          Step {STEPS.indexOf("Patient & Billing") + 1} (Patient &amp; Billing) before submitting — the patient has no way to receive their signing link
                          otherwise, and this order cannot be placed until one is added.
                        </Typography>
                      </Box>
                    )}

                    <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1, mt: 3 }}>Supporting Documents (optional)</Typography>
                    <Typography sx={{ fontSize: 13.5, color: (isDark ? "#94a3b8" : "#64748b"), mb: 1.5 }}>
                      Attach a patient ID, insurance card, driver's license, ABN, prior auth, or anything else relevant
                      to this order. Entirely optional — it never affects whether this order can be placed. Files
                      upload right after the order is submitted; lab staff can add more to this order later.
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                      {supportingDocRows.map((row) => (
                        <Box key={row._key} sx={{
                          display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1,
                          p: 1.25, borderRadius: "10px",
                          border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,53,122,0.1)",
                          bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,53,122,0.02)",
                        }}>
                          <Select
                            size="small" displayEmpty value={row.docType}
                            onChange={(e) => updateSupportingDocRow(row._key, { docType: e.target.value })}
                            sx={{ minWidth: 170, bgcolor: isDark ? "#111827" : "#fff", borderRadius: "8px", fontSize: 13.5 }}
                          >
                            <MenuItem value="" disabled><em>Document type</em></MenuItem>
                            {SUPPORTING_DOC_TYPE_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13.5 }}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          {row.docType === "other" && (
                            <TextField
                              size="small" placeholder="Describe this document"
                              value={row.otherLabel}
                              onChange={(e) => updateSupportingDocRow(row._key, { otherLabel: e.target.value })}
                              sx={{ minWidth: 180, "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: isDark ? "#111827" : "#fff", fontSize: 13.5 } }}
                            />
                          )}
                          <Button component="label" size="small" variant="outlined"
                            sx={{ textTransform: "none", borderColor: isDark ? "rgba(255,255,255,0.25)" : "rgba(0,53,122,0.25)", color: isDark ? BRAND.gold : BRAND.navy, fontSize: 12.5 }}>
                            {row.file ? row.file.name : "Choose file"}
                            <input type="file" hidden accept="application/pdf,image/*"
                              onChange={(e) => updateSupportingDocRow(row._key, { file: e.target.files?.[0] || null })} />
                          </Button>
                          {supportingDocRows.length > 1 && (
                            <IconButton size="small" onClick={() => removeSupportingDocRow(row._key)} sx={{ ml: "auto", color: (isDark ? "#94a3b8" : "#64748b") }}>
                              <Close sx={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                          {row.file && !row.docType && (
                            <Typography sx={{ fontSize: 12, color: "#dc2626", width: "100%" }}>
                              Choose a document type, or this file won't be uploaded.
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                    <Button size="small" onClick={addSupportingDocRow}
                      sx={{ mt: 1, textTransform: "none", fontSize: 13, fontWeight: 700, color: isDark ? BRAND.gold : BRAND.navy }}>
                      + Add Another Document
                    </Button>
                  </Box>
                )}

                {/* 2026-10-13 fix — real gap caught by review: Step 4's
                    required-field gate now auto-expands whichever
                    accordion has something missing (see above), but a
                    clinician who scrolled past that point still just saw
                    a greyed-out Next with no text explaining why — the
                    same "just a blocking button" complaint in miniature.
                    This names the count out loud, right next to the
                    button it's blocking. */}
                {step === 3 && step3MissingFieldCount > 0 && (
                  <Alert severity="warning" sx={{ mt: 3, borderRadius: 2 }}>
                    {step3MissingFieldCount} required field{step3MissingFieldCount > 1 ? "s" : ""} still empty — see the highlighted section{step3MissingFieldCount > 1 ? "s" : ""} above.
                  </Alert>
                )}

                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4, pt: 3, borderTop: "1px solid rgba(0,53,122,0.08)" }}>
                  <Button onClick={back} disabled={step === 0} sx={{ textTransform: "none", color: (isDark ? "#94a3b8" : "#64748b"), fontWeight: 600 }}>Back</Button>
                  {step < STEPS.length - 1 ? (
                    <Button variant="contained" disabled={!canProceedFromStep(step)} onClick={next}
                      sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", px: 3, bgcolor: BRAND.navy, boxShadow: "0 4px 14px rgba(0,53,122,0.25)", "&:hover": { bgcolor: BRAND.navyDark } }}>
                      Next
                    </Button>
                  ) : (
                    <Button variant="contained"
                      disabled={submitting || !(selectedPatient?.email || selectedPatient?.phone)
                        || !!blockedSelectionMessage || effectiveMissingTestIds.length > 0}
                      onClick={handleSubmitOrder}
                      sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", px: 3, bgcolor: BRAND.navy, boxShadow: "0 4px 14px rgba(0,53,122,0.25)", "&:hover": { bgcolor: BRAND.navyDark } }}>
                      {submitting ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Place Order"}
                    </Button>
                  )}
                </Box>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );
}
